import React, { useState, useEffect, useRef } from 'react';
import { useLang } from '../context/LanguageContext';
import { getDistance, getTariffs, estimateRide } from '../services/api';
import RideMap from './RideMap';
import AddressInput from './AddressInput';
import { tariffDisplayName } from '../utils/tariffNames';

export default function RideForm({ onSubmit, loading }) {
  const [from, setFrom]               = useState({ text: '', lat: null, lng: null });
  const [to, setTo]                   = useState({ text: '', lat: null, lng: null });
  const [distanceKm, setDistanceKm]   = useState('');
  const [calculating, setCalculating] = useState(false);
  const [distanceError, setDistanceError] = useState('');
  const [tariffs, setTariffs]         = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [estimate, setEstimate]       = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateFailed, setEstimateFailed] = useState(false);
  const [estimateRetry, setEstimateRetry] = useState(0);
  const { t } = useLang();
  const rf = t.rideForm;

  const hasCoords = !!(from.lat && to.lat);
  const km = parseFloat(distanceKm) || 5;

  useEffect(() => {
    getTariffs()
      .then((res) => {
        setTariffs(res.data);
        if (res.data.length > 0) setSelectedClass(res.data[0].code);
      })
      .catch(() => {});
  }, []);

  // Real price + ETA from the backend once we have coordinates for both points - this is
  // also what fixes the distance field so it matches what will actually be charged.
  useEffect(() => {
    if (!from.lat || !to.lat) {
      setEstimate(null);
      setEstimateFailed(false);
      setEstimateLoading(false);
      return undefined;
    }
    let cancelled = false;
    setEstimateLoading(true);
    setEstimateFailed(false);
    estimateRide(from.lat, from.lng, to.lat, to.lng)
      .then((res) => {
        if (cancelled) return;
        setEstimate(res.data);
        setDistanceKm(String(res.data.distanceKm));
        setDistanceError('');
      })
      .catch(() => {
        if (cancelled) return;
        setEstimate(null);
        setEstimateFailed(true);
      })
      .finally(() => { if (!cancelled) setEstimateLoading(false); });
    return () => { cancelled = true; };
  }, [from.lat, from.lng, to.lat, to.lng, estimateRetry]);

  const abortRef = useRef(null);

  const handleFromSelect = ({ text, lat, lng }) => setFrom({ text, lat, lng });
  const handleToSelect   = ({ text, lat, lng }) => setTo({ text, lat, lng });

  // Fallback: called on blur when user typed manually without selecting from dropdown
  const handleBlurFallback = async () => {
    if (!from.text.trim() || !to.text.trim()) return;
    if (from.lat && to.lat) return; // already have coords from autocomplete

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const { signal } = abortRef.current;

    setCalculating(true);
    setDistanceError('');
    try {
      const res = await getDistance(from.text, to.text, signal);
      setDistanceKm(String(res.data.distanceKm));
      if (res.data.fromCoords) setFrom((p) => ({ ...p, lat: res.data.fromCoords[0], lng: res.data.fromCoords[1] }));
      if (res.data.toCoords)   setTo((p)   => ({ ...p, lat: res.data.toCoords[0],   lng: res.data.toCoords[1]   }));
    } catch (err) {
      if (err.code === 'ERR_CANCELED') return;
      setDistanceError(t.booking?.distanceError || 'Could not calculate distance. Enter manually.');
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSubmit({
        fromLocation: from.text,
        toLocation: to.text,
        distanceKm: distanceKm ? parseFloat(distanceKm) : undefined,
        fromLat: from.lat || undefined,
        fromLng: from.lng || undefined,
        toLat: to.lat || undefined,
        toLng: to.lng || undefined,
        rideClass: selectedClass || undefined,
      });
      setFrom({ text: '', lat: null, lng: null });
      setTo({ text: '', lat: null, lng: null });
      setDistanceKm('');
    } catch {
      // Booking failed - keep the form filled in so the user doesn't have to retype it
    }
  };

  const coords = from.lat && to.lat ? { from: [from.lat, from.lng], to: [to.lat, to.lng] } : null;

  return (
    <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 space-y-5">
      <h2 className="text-xl font-black text-white">{rf.title}</h2>

      <div className="bg-white/10 rounded-2xl p-4 space-y-3 border border-white/10">
        <div className="flex items-center gap-3">
          <span className="w-3 h-3 rounded-full bg-green-400 shrink-0" />
          <AddressInput
            value={from.text}
            onChange={(text) => { setFrom({ text, lat: null, lng: null }); setDistanceKm(''); }}
            onSelect={handleFromSelect}
            onBlur={handleBlurFallback}
            placeholder={rf.pickup}
            required
          />
        </div>
        <div className="ml-1.5 border-l-2 border-dashed border-white/20 h-4" />
        <div className="flex items-center gap-3">
          <span className="w-3 h-3 rounded-full bg-red-400 shrink-0" />
          <AddressInput
            value={to.text}
            onChange={(text) => { setTo({ text, lat: null, lng: null }); setDistanceKm(''); }}
            onSelect={handleToSelect}
            onBlur={handleBlurFallback}
            placeholder={rf.dropoff}
            required
          />
        </div>
      </div>

      {coords && (
        <RideMap
          fromCoords={coords.from}
          toCoords={coords.to}
          fromLabel={from.text}
          toLabel={to.text}
        />
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-white/60 mb-0">
            {rf.distLabel} <span className="text-white/30 font-normal">{rf.distHint}</span>
          </label>
          {calculating && (
            <span className="text-xs text-primary-400 flex items-center gap-1">
              <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              {t.booking?.calculating || 'Calculating…'}
            </span>
          )}
        </div>
        <input
          value={distanceKm}
          onChange={(e) => { setDistanceKm(e.target.value); setDistanceError(''); }}
          type="text"
          inputMode="decimal"
          pattern="[0-9]*\.?[0-9]*"
          placeholder={rf.distPlaceholder}
          readOnly={calculating || hasCoords}
          className={`w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${calculating || hasCoords ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
        {distanceError && (
          <p className="text-xs text-amber-400 mt-1">{distanceError}</p>
        )}
      </div>

      {from.text && to.text && tariffs.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-white/60 mb-2">{rf.tariffLabel}</label>

          {hasCoords && estimateLoading && (
            <p className="text-xs text-primary-400 flex items-center gap-1.5">
              <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              {t.booking?.calculating || 'Calculating…'}
            </p>
          )}

          {hasCoords && estimateFailed && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
              <p className="text-sm text-amber-400">{rf.estimateError}</p>
              <button
                type="button"
                onClick={() => setEstimateRetry((n) => n + 1)}
                className="text-xs font-semibold text-amber-300 underline mt-1"
              >
                {rf.retry}
              </button>
            </div>
          )}

          {!hasCoords && !distanceKm && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {tariffs.map((tariff) => (
                  <div key={tariff.code} className="text-left rounded-xl border-2 border-white/10 bg-white/5 px-4 py-3 opacity-50">
                    <div className="text-sm font-semibold text-white">{tariffDisplayName(tariff, t)}</div>
                    <div className="text-lg font-black text-white/40">€—</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-white/30 mt-2">{rf.selectAddressesHint}</p>
            </>
          )}

          {(hasCoords ? estimate && !estimateLoading : !!distanceKm) && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {tariffs.map((tariff) => {
                  const matched = estimate?.tariffs.find((e) => e.code === tariff.code);
                  const tariffPrice = matched ? matched.price : (km * tariff.pricePerKm).toFixed(2);
                  const active = selectedClass === tariff.code;
                  return (
                    <button
                      type="button"
                      key={tariff.code}
                      onClick={() => setSelectedClass(tariff.code)}
                      className={`text-left rounded-xl border-2 px-4 py-3 transition-colors ${
                        active ? 'border-primary-500 bg-primary-600/20' : 'border-white/20 bg-white/5 hover:border-white/40'
                      }`}
                    >
                      <div className="text-sm font-semibold text-white">{tariffDisplayName(tariff, t)}</div>
                      <div className="text-lg font-black text-primary-400">€{tariffPrice}</div>
                    </button>
                  );
                })}
              </div>
              {!hasCoords && (
                <p className="text-xs text-white/30 mt-2">{rf.estimateApprox}</p>
              )}
              {estimate?.etaMin != null && (
                <p className="text-xs text-white/40 mt-2">
                  {(rf.etaLabel || '~{min} min trip').replace('{min}', Math.round(estimate.etaMin))}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || calculating || (hasCoords ? (estimateLoading || estimateFailed) : !distanceKm)}
        className="btn-primary w-full justify-center py-3.5 disabled:opacity-50 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary-600/30"
      >
        {loading ? (
          <>
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            {rf.booking}
          </>
        ) : rf.confirm}
      </button>
    </form>
  );
}
